import { CSSProperties, useEffect, useMemo, useRef, useState } from "react";

interface CenterEllipseTextProps {
  id: string;
  titles: string[];
  separator?: string;
  TextStyle?: CSSProperties;
  shrink: boolean;
}

const TITLE_MARGIN = 8;
const MIN_CHARS_TO_PRESERVE_MIDDLE = 10; // Min chars before/after ellipsis for middle truncation

export default function CenterEllipseText({
  id,
  titles,
  separator = "on",
  TextStyle,
  shrink,
}: CenterEllipseTextProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState<number>(0);
  const fontSize = (shrink ? "18px" : TextStyle?.fontSize) || "16px";

  const combinedTitle = useMemo(() => {
    if (!Array.isArray(titles)) return "";

    return titles.filter(Boolean).join(` ${separator} `);
  }, [titles, separator]);

  useEffect(() => {
    const element = containerRef.current;
    if (!element) return;

    const updateWidth = () => {
      // Subtract padding/margins if necessary for accurate content width,
      // or just use offsetWidth if span will fill available space.
      // Let's use offsetWidth for simplicity, assuming flexGrow handles it.
      setContainerWidth(element.offsetWidth);
    };

    const resizeObserver = new ResizeObserver(updateWidth);
    resizeObserver.observe(element);
    updateWidth(); // Initial measure

    return () => {
      resizeObserver.unobserve(element);
    };
  }, []);

  // --- Middle Truncation Logic ---
  const displayTitle = useMemo(() => {
    if (!containerWidth || !combinedTitle || !TextStyle) {
      return combinedTitle; // Not ready or nothing to truncate
    }

    const canvas = document.createElement("canvas");
    const context = canvas.getContext("2d");
    if (!context) return combinedTitle; // Cannot measure

    // Apply font styles for accurate measurement
    const fontWeight = TextStyle.fontWeight || "normal";
    const fontFamily = TextStyle.fontFamily || "sans-serif";
    context.font = `${fontWeight} ${fontSize} ${fontFamily}`;

    const avgCharWidth =
      context.measureText(combinedTitle).width / combinedTitle.length || 10; // Estimate average char width

    // Estimate how many characters fit in TWO lines
    // Subtract space for the copy icon roughly (approx 30-40px depending on margins)
    const effectiveWidth = Math.max(0, containerWidth - TITLE_MARGIN * 2 - 30); // Conservative estimate
    const maxCharsInTwoLines = Math.floor((effectiveWidth * 2) / avgCharWidth);

    // If the title is short enough for ~two lines, render it fully and let CSS clamp
    if (combinedTitle.length <= maxCharsInTwoLines) {
      return combinedTitle;
    }

    // --- Title exceeds ~two lines: Apply Middle Truncation ---
    // Target character count is based on what fits in two lines, minus ellipsis
    const targetChars = Math.max(0, maxCharsInTwoLines - 3); // Reserve 3 for "..."

    if (targetChars <= MIN_CHARS_TO_PRESERVE_MIDDLE * 2) {
      // Not enough space even for minimal middle truncation, fallback to full title
      // CSS clamp will handle the end ellipsis in this edge case
      return combinedTitle;
    } else {
      const charsPerSide = Math.floor(targetChars / 2);
      const start = combinedTitle.substring(0, charsPerSide);
      const end = combinedTitle.substring(combinedTitle.length - charsPerSide);
      return `${start}...${end}`;
    }
  }, [combinedTitle, containerWidth, TextStyle, shrink]);

  return (
    <div
      id={id}
      ref={containerRef}
      style={{
        display: "flex",
        alignItems: "center",
        width: "100%",
        overflow: "hidden",
        ...TextStyle,
      }}
    >
      <span
        style={{
          display: "-webkit-box",
          WebkitBoxOrient: "vertical",
          WebkitLineClamp: 2,
          overflow: "hidden",
          textOverflow: "ellipsis",
          lineHeight: TextStyle?.lineHeight || "normal",
          fontSize,
          fontWeight: TextStyle?.fontWeight,
          minWidth: 0,
          flexGrow: 1,
          marginRight: `${TITLE_MARGIN}px`,
          transition: "all 0.35s ease-in-out",
        }}
        title={combinedTitle}
      >
        {displayTitle}
      </span>
    </div>
  );
}
